import { getLoadings } from '@shakkerai/pro-store';
import { getViewport } from '@starflow/app/store';
import { notifySuccess, notifyError } from '@shakkerai/pro-components';
import { uploadImage, forceRenameToPng } from '@shakkerai/pro-utils';
import { translate } from '@shakkerai/util-i18n';
import { updateElement } from '@starflow/app/helpers/canvas';

export const cropImage = async (id, innerCrop, element) => {
  if (!element || element.type !== 'image') return;

  const { startLoading, stopLoading } = getLoadings();
  const scale = getViewport().scale;

  try {
    startLoading(id + '-cropping');
    const outerCrop = calcImageCrop(element.width, element.height, element.scaleX, element.scaleY);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const outerImage = new Image();
    outerImage.src = element.url;
    outerImage.crossOrigin = 'anonymous';
    await new Promise((resolve) => (outerImage.onload = resolve));

    canvas.width = outerCrop.width;
    canvas.height = outerCrop.height;

    ctx.drawImage(
      outerImage,
      outerCrop.x,
      outerCrop.y,
      outerCrop.width,
      outerCrop.height,
      0,
      0,
      outerCrop.width,
      outerCrop.height,
    );

    const innerImageBlob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    const innerImageURL = URL.createObjectURL(innerImageBlob);
    const innerCanvas = document.createElement('canvas');
    const innerCtx = innerCanvas.getContext('2d');

    const innerImage = new Image();
    innerImage.src = innerImageURL;

    await new Promise((resolve) => (innerImage.onload = resolve));
    URL.revokeObjectURL(innerImageURL);

    let { x, y, width, height } = innerCrop;
    x /= scale;
    y /= scale;
    width /= scale;
    height /= scale;

    innerCanvas.width = width; /// element.scaleX;
    innerCanvas.height = height; /// element.scaleY;

    const ratio = element.scaleX / element.scaleY;

    innerCtx.drawImage(
      innerImage,
      (x / element.scaleX) * Math.min(1, ratio),
      y / element.scaleY / Math.max(1, ratio),
      width / element.scaleX,
      height / element.scaleY,
      0,
      0,
      width / Math.min(1, ratio),
      height * Math.max(1, ratio),
    );

    const blob = await new Promise((resolve) => innerCanvas.toBlob(resolve, 'image/png'));
    const file = new File([blob], forceRenameToPng(element.name), { type: 'image/png' });
    const result = await uploadImage(file);

    if (!result?.url) {
      throw new Error(translate('upload_fail_toast'));
    }

    const url = result.url;

    updateElement(element.id, (element) => {
      return {
        ...element,
        width,
        height,
        x: element.x + x,
        y: element.y + y,
        url,
        modifiers: (element.modifiers || []).concat({
          url,
          type: 'crop',
          data: innerCrop,
        }),
      };
    });

    notifySuccess(translate('image_crop_success'));
  } catch (error) {
    notifyError(translate('image_crop_error'), error.message);
  } finally {
    stopLoading(id + '-cropping');
  }
};

export const calcImageCrop = (width, height, scaleX, scaleY) => {
  const frameWidth = width * scaleX;
  const frameHeight = height * scaleY;
  const frameAspectRatio = Math.abs(frameWidth / frameHeight);
  const imageAspectRatio = Math.abs(width / height);

  let newWidth;
  let newHeight;

  if (frameAspectRatio >= imageAspectRatio) {
    newWidth = width;
    newHeight = width / frameAspectRatio;
  } else {
    newWidth = height * frameAspectRatio;
    newHeight = height;
  }

  return {
    x: (width - newWidth) / 2,
    y: (height - newHeight) / 2,
    width: Math.round(newWidth),
    height: Math.round(newHeight),
    frameAspectRatio,
    imageAspectRatio,
  };
};
